<template>
  <v-main>
    <v-container fluid>

      <!-- Single unified settings card -->
      <v-card elevation="3" class="settings-card">

        <!-- ── Card Header ── -->
        <div class="settings-header">
          <div class="d-flex align-center">
            <div class="header-icon-wrap mr-3">
              <v-icon color="white" size="22">mdi-domain</v-icon>
            </div>
            <div>
              <h1 class="header-title">Company Settings</h1>
              <p class="header-subtitle">Manage company details, branding, commissions and content</p>
            </div>
          </div>
          <div class="d-flex align-center">
            <v-chip
              :color="isEditMode ? 'success' : 'blue-grey'"
              dark
              small
              class="mr-3"
            >
              <v-icon left small>{{ isEditMode ? 'mdi-pencil' : 'mdi-eye' }}</v-icon>
              {{ isEditMode ? 'Edit Mode' : 'Read Only' }}
            </v-chip>
            <v-btn
              :color="isEditMode ? 'warning' : 'primary'"
              :outlined="!isEditMode"
              @click="toggleEditMode"
              small
              class="mr-2"
            >
              <v-icon left small>{{ isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
              {{ isEditMode ? 'Read Only' : 'Edit Mode' }}
            </v-btn>
            <v-btn
              v-if="roleName == 'SUPER ADMIN' && company.id == null"
              color="grey darken-1"
              outlined
              small
              @click="$router.back()"
            >
              <v-icon left small>mdi-arrow-left</v-icon>
              Back
            </v-btn>
          </div>
        </div>

        <v-card-text class="pa-0">

          <!-- ══ SECTION 1: Basic Information ══ -->
          <div class="section-block">
            <div class="section-label">
              <v-icon size="16" class="mr-1" color="primary">mdi-information-outline</v-icon>
              Basic Information
            </div>
            <v-row class="ma-0">
              <v-col cols="12" sm="4" class="field-col">
                <v-text-field
                  v-model="form.name"
                  outlined dense
                  label="Company Name *"
                  prepend-inner-icon="mdi-domain"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.name"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" class="field-col">
                <v-text-field
                  v-model="form.tag_line"
                  outlined dense
                  label="Tagline"
                  prepend-inner-icon="mdi-format-quote-open"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.tag_line"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" class="field-col">
                <v-text-field
                  v-model="form.email"
                  outlined dense
                  label="Email *"
                  prepend-inner-icon="mdi-email-outline"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.email"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" class="field-col">
                <v-text-field
                  v-model="form.phone"
                  outlined dense
                  label="Phone *"
                  prepend-inner-icon="mdi-phone-outline"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.phone"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" class="field-col">
                <v-text-field
                  v-model="form.admin_name"
                  outlined dense
                  label="Admin Name"
                  prepend-inner-icon="mdi-account-tie-outline"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" class="field-col">
                <v-text-field
                  v-model="form.address"
                  outlined dense
                  label="Address *"
                  prepend-inner-icon="mdi-map-marker-outline"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.address"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" class="field-col">
                <v-text-field
                  v-model="form.state_id"
                  outlined dense
                  label="State"
                  prepend-inner-icon="mdi-map-outline"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.state_id"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" class="field-col">
                <v-text-field
                  v-model="form.city_id"
                  outlined dense
                  label="City"
                  prepend-inner-icon="mdi-city-variant-outline"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.city_id"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" class="field-col">
                <v-text-field
                  v-model="form.pincode"
                  outlined dense
                  label="Pincode"
                  prepend-inner-icon="mdi-mailbox-outline"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.pincode"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <v-divider></v-divider>

          <!-- ══ SECTION 2: Business Configuration ══ -->
          <div class="section-block">
            <div class="section-label">
              <v-icon size="16" class="mr-1" color="orange darken-2">mdi-cog-outline</v-icon>
              Business Configuration
            </div>
            <v-row class="ma-0">
              <v-col cols="12" sm="3" class="field-col">
                <v-text-field
                  v-model="form.tax_percent"
                  outlined dense
                  label="Tax Percent (%)"
                  prepend-inner-icon="mdi-receipt-text-outline"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.tax_percent"
                  hide-details="auto"
                  suffix="%"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3" class="field-col">
                <v-text-field
                  v-model="form.packaging_cost"
                  outlined dense
                  label="Packaging Cost"
                  prepend-inner-icon="mdi-package-variant-closed"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.packaging_cost"
                  hide-details="auto"
                  prefix="₹"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3" class="field-col">
                <v-text-field
                  v-model.number="form.consultation_commission"
                  outlined dense
                  label="Consultation Commission"
                  prepend-inner-icon="mdi-stethoscope"
                  type="number" min="0" max="100" step="0.01"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.consultation_commission"
                  :rules="consultationCommissionRules"
                  hide-details="auto"
                  suffix="%"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3" class="field-col">
                <v-text-field
                  v-model.number="form.appointment_commission"
                  outlined dense
                  label="Appointment Commission"
                  prepend-inner-icon="mdi-calendar-check-outline"
                  type="number" min="0" max="100" step="0.01"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.appointment_commission"
                  hide-details="auto"
                  suffix="%"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3" class="field-col">
                <v-text-field
                  v-model.number="form.doctor_share_percent"
                  outlined dense
                  label="Doctor Share %"
                  prepend-inner-icon="mdi-doctor"
                  type="number" min="0" max="100" step="0.01"
                  :disabled="!isEditMode || !canEditDoctorShare"
                  :readonly="!isEditMode || !canEditDoctorShare"
                  :error-messages="errors.doctor_share_percent"
                  hide-details="auto"
                  suffix="%"
                  :hint="canEditDoctorShare ? 'Default 50%. Admin & Super Admin only.' : 'Only admin can edit'"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3" class="field-col">
                <v-text-field
                  v-model.number="form.tds_percent"
                  outlined dense
                  label="Doctor TDS %"
                  prepend-inner-icon="mdi-percent"
                  type="number" min="0" max="100" step="0.01"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.tds_percent"
                  hide-details="auto"
                  suffix="%"
                  hint="TDS deducted from doctor gross share (1% or 10%)"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3" class="field-col">
                <v-text-field
                  v-model.number="form.cod_extra_charge"
                  outlined dense
                  label="COD Extra Charge"
                  prepend-inner-icon="mdi-cash-multiple"
                  type="number" min="0" step="0.01"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.cod_extra_charge"
                  hide-details="auto"
                  prefix="₹"
                  hint="Extra courier charge added when a customer pays Cash on Delivery"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3" class="field-col">
                <v-text-field
                  v-model.number="form.delivery_charges"
                  outlined dense
                  label="Delivery Charges"
                  prepend-inner-icon="mdi-truck-delivery-outline"
                  type="number" min="0" step="0.01"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.delivery_charges"
                  hide-details="auto"
                  prefix="₹"
                  hint="Charged per seller package when its subtotal is below ₹500; free above that"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <v-divider></v-divider>

          <!-- ══ SECTION: Video Call Settings (ZegoCloud) ══ -->
          <div v-if="canEditDoctorShare" class="section-block">
            <div class="section-label">
              <v-icon size="16" class="mr-1" color="teal">mdi-video-outline</v-icon>
              Video Call Settings
            </div>
            <v-row class="ma-0">
              <v-col cols="12" sm="4" class="field-col">
                <v-text-field
                  v-model="form.zego_app_id"
                  outlined dense
                  label="ZegoCloud App ID"
                  prepend-inner-icon="mdi-identifier"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.zego_app_id"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="8" class="field-col">
                <v-text-field
                  v-model="form.zego_app_sign"
                  outlined dense
                  label="ZegoCloud App Sign"
                  prepend-inner-icon="mdi-key-outline"
                  :type="showZegoSign ? 'text' : 'password'"
                  :append-icon="showZegoSign ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="showZegoSign = !showZegoSign"
                  autocomplete="new-password"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.zego_app_sign"
                  :placeholder="form.has_zego_app_sign ? '•••••••• (saved)' : ''"
                  :hint="form.has_zego_app_sign ? 'Leave blank to keep the saved App Sign. The saved value is never shown.' : 'Not set — server falls back to its environment config.'"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <v-divider></v-divider>

          <!-- ══ SECTION 3: App Versions & Logo ══ -->
          <div class="section-block">
            <div class="section-label">
              <v-icon size="16" class="mr-1" color="purple">mdi-cellphone-cog</v-icon>
              App Versions &amp; Logo
            </div>
            <v-row class="ma-0" align="center">
              <v-col cols="12" sm="3" class="field-col">
                <v-text-field
                  v-model="form.android_version"
                  outlined dense
                  label="Android Version"
                  prepend-inner-icon="mdi-android"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.android_version"
                  placeholder="e.g. 1.0.0"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3" class="field-col">
                <v-text-field
                  v-model="form.ios_version"
                  outlined dense
                  label="iOS Version"
                  prepend-inner-icon="mdi-apple"
                  :disabled="!isEditMode" :readonly="!isEditMode"
                  :error-messages="errors.ios_version"
                  placeholder="e.g. 1.0.0"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" class="field-col">
                <div class="logo-upload-area" :class="{ 'logo-disabled': !isEditMode }">
                  <div class="d-flex align-center">
                    <!-- Logo preview -->
                    <div class="logo-preview-wrap mr-4">
                      <img v-if="image_url" :src="image_url" class="logo-preview-img" />
                      <div v-else class="logo-placeholder">
                        <v-icon size="32" color="grey lighten-1">mdi-image-outline</v-icon>
                      </div>
                    </div>
                    <!-- Upload controls -->
                    <div>
                      <p class="text-caption grey--text mb-1">Company Logo</p>
                      <div class="d-flex align-center">
                        <v-btn
                          x-small
                          color="primary"
                          outlined
                          :disabled="!isEditMode"
                          @click="$refs.file.click()"
                          class="mr-2"
                        >
                          <v-icon left x-small>mdi-upload</v-icon>
                          Upload
                        </v-btn>
                        <v-btn
                          x-small
                          color="error"
                          outlined
                          :disabled="!isEditMode || !image_url"
                          @click="($refs.file.value = null), (image_url = null)"
                        >
                          <v-icon left x-small>mdi-trash-can-outline</v-icon>
                          Remove
                        </v-btn>
                      </div>
                      <p class="text-caption grey--text mt-1 mb-0">PNG or JPG, recommended 200×200</p>
                    </div>
                  </div>
                  <input
                    type="file" id="file" name="file"
                    :ref="`file`"
                    @change="onFileChange"
                    :disabled="!isEditMode"
                    style="display: none"
                  />
                </div>
              </v-col>
            </v-row>
          </div>

          <v-divider></v-divider>

          <!-- ══ SECTION 4: Content & Legal ══ -->
          <div class="section-block section-block--content">
            <div class="section-label">
              <v-icon size="16" class="mr-1" color="deep-orange">mdi-file-document-outline</v-icon>
              Content &amp; Legal
              <span class="ml-auto text-caption grey--text font-weight-regular" style="text-transform:none;letter-spacing:0">
                {{ openPanels.length }} of {{ contentPanels.length }} expanded
              </span>
            </div>

            <div
              v-for="panel in contentPanels"
              :key="panel.key"
              class="accordion-item"
              :class="{ 'accordion-item--open': openPanels.includes(panel.key) }"
            >
              <!-- Accordion header -->
              <div class="accordion-header" @click="togglePanel(panel.key)">
                <div class="d-flex align-center">
                  <v-icon size="16" class="mr-2" :color="panel.color">{{ panel.icon }}</v-icon>
                  <span class="accordion-title">{{ panel.label }}</span>
                  <v-chip
                    v-if="form[panel.field] && form[panel.field].replace(/<[^>]*>/g,'').trim()"
                    x-small
                    color="success"
                    outlined
                    class="ml-2"
                  >filled</v-chip>
                  <v-chip v-else x-small color="grey" outlined class="ml-2">empty</v-chip>
                </div>
                <v-icon
                  size="18"
                  color="grey darken-1"
                  class="accordion-chevron"
                  :class="{ 'accordion-chevron--open': openPanels.includes(panel.key) }"
                >mdi-chevron-down</v-icon>
              </div>

              <!-- Accordion body -->
              <v-expand-transition>
                <div v-show="openPanels.includes(panel.key)" class="accordion-body">
                  <vue-editor
                    :ref="panel.key"
                    v-model="form[panel.field]"
                    class="form-control"
                    :disabled="!isEditMode"
                  />
                </div>
              </v-expand-transition>
            </div>
          </div>

        </v-card-text>

        <!-- ── Sticky Save Footer ── -->
        <div v-if="isEditMode" class="save-footer">
          <v-btn
            color="grey darken-1"
            outlined
            small
            @click="$router.back()"
            :disabled="isLoading"
            class="mr-3"
          >
            <v-icon left small>mdi-close</v-icon>Cancel
          </v-btn>
          <v-btn
            color="primary"
            small
            @click="save"
            :loading="isLoading"
            :disabled="isLoading"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save Changes
          </v-btn>
        </div>

      </v-card>

    </v-container>

    <!-- Version Update Confirmation Dialog -->
    <v-dialog v-model="versionConfirmDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon color="warning" class="mr-2">mdi-cellphone-arrow-down</v-icon>
          Confirm Version Update
        </v-card-title>
        <v-card-text>
          <p class="mb-3">You are about to update the app version(s). This will affect all users on update checks.</p>
          <v-simple-table dense>
            <tbody>
              <tr v-if="old_json.android_version !== form.android_version">
                <td class="font-weight-medium">Android</td>
                <td class="grey--text">{{ old_json.android_version || '—' }}</td>
                <td><v-icon small>mdi-arrow-right</v-icon></td>
                <td class="success--text font-weight-bold">{{ form.android_version }}</td>
              </tr>
              <tr v-if="old_json.ios_version !== form.ios_version">
                <td class="font-weight-medium">iOS</td>
                <td class="grey--text">{{ old_json.ios_version || '—' }}</td>
                <td><v-icon small>mdi-arrow-right</v-icon></td>
                <td class="success--text font-weight-bold">{{ form.ios_version }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="versionConfirmDialog = false" :disabled="isLoading">Cancel</v-btn>
          <v-btn color="primary" @click="confirmSave" :loading="isLoading">Confirm & Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>
<script>
import { VueEditor } from "vue2-editor";
import axios from "@/axios";
import { mapGetters } from "vuex";
export default {
  name: "CompanyEditPage",
  components: {
    VueEditor,
  },
  computed: {
    ...mapGetters("auth", ["isSuperAdmin", "isAdmin", "roleName"]),
    canEditDoctorShare() {
      return this.isSuperAdmin || this.isAdmin;
    },
  },
  data() {
    return {
      form: {
        name: "",
        is_active: true,
        consultation_commission: 0,
        appointment_commission: 0,
        doctor_share_percent: 50,
        tds_percent: 0.10,
        cod_extra_charge: 50,
        delivery_charges: 50,
      },
      image_url: "",
      old_json: {},
      isLoading: false,
      isEditMode: false,
      showZegoSign: false,
      errors: {},
      versionConfirmDialog: false,
      openPanels: [],
      contentPanels: [
        { key: 'about_us',         field: 'about_us',         label: 'About Us',            icon: 'mdi-information-variant',       color: 'indigo' },
        { key: 'terms_conditions', field: 'terms_conditions', label: 'Terms & Conditions',  icon: 'mdi-file-sign',                 color: 'indigo' },
        { key: 'faqs',             field: 'faqs',             label: 'FAQs',                icon: 'mdi-frequently-asked-questions', color: 'indigo' },
        { key: 'privacy_policy',   field: 'privacy_policy',   label: 'Privacy Policy',      icon: 'mdi-shield-check-outline',      color: 'indigo' },
        { key: 'disclaimer',       field: 'disclaimer',       label: 'Disclaimer',           icon: 'mdi-alert-circle-outline',      color: 'indigo' },
      ],
      consultationCommissionRules: [
        v => {
          if (v === null || v === undefined || v === '') return true;
          const num = parseFloat(v);
          return (!isNaN(num) && num >= 0 && num <= 100) || 'Commission must be between 0 and 100%';
        }
      ],
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    togglePanel(key) {
      const idx = this.openPanels.indexOf(key);
      if (idx === -1) this.openPanels.push(key);
      else this.openPanels.splice(idx, 1);
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      // Show a message to the user about the mode change
      if (this.isEditMode) {
        this.$toast.success("Edit mode enabled. You can now modify the form.");
      } else {
        this.$toast.info(
          "Read-only mode enabled. Form is now protected from changes."
        );
      }
    },

    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.companyId;
      await axios
        .get(`/companies/${encryptedId}`)
        .then((response) => {
          this.form = response.data.data;
          // App Sign is never returned by the API; keep the field reactive for typing
          this.$set(this.form, "zego_app_sign", "");
          this.old_json = { ...response.data.data };
          this.image_url = this.form.logo_path
            ? `${this.mediaUrl + this.form.logo_path}`
            : "";
        })
        .catch((error) => {
          // redirect to Home Page
          window.location.href = "/auth/login";
          console.error("Error fetching data:", error);
        });
      this.isLoading = false;
    },
    async save() {
      const versionChanged =
        this.old_json.android_version !== this.form.android_version ||
        this.old_json.ios_version !== this.form.ios_version;

      if (versionChanged) {
        this.versionConfirmDialog = true;
        return;
      }

      await this.doSave();
    },
    async confirmSave() {
      this.versionConfirmDialog = false;
      await this.doSave();
    },
    async doSave() {
      try {
        this.isLoading = true;
        await axios.patch(
          `/companies/${this.$route.params.companyId}`,
          this.form
        );
        await this.handleFileUpload();
        await this.saveUserTimestamp(this.old_json, this.form);
        // Stay on the page: reload the saved data, refresh the logged-in user's
        // company (logo / name in the layout), then confirm with a toast.
        await this.getData();
        await this.$store.dispatch("auth/attempt", localStorage.getItem("token"));
        if (this.$refs.file) this.$refs.file.value = null; // don't re-upload the same logo on the next save
        this.isEditMode = false;
        this.isLoading = false;
        this.$toast.success("Company updated successfully");
      } catch (e) {
        console.log(e);
        this.isLoading = false;
      }
    },
    async handleFileUpload() {
      console.log("called");
      if (this.$refs.file != null) {
        console.log("uploading");
        let attachment = this.$refs.file.files[0];
        let formData = new FormData();
        formData.append("id", this.form.id);
        formData.append("logo_path", attachment);
        await axios
          .post("upload_company_logo_path", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .catch(function (error) {
            console.log(error);
            console.log("FAILURE!!");
          });
      }
    },
    onFileChange() {
      this.warningImageLoader = false;
      this.file = this.$refs.file?.files[0];
      let reader = new FileReader();
      reader.readAsDataURL(this.file);
      reader.onload = (evt) => {
        let img = new Image();
        img.onload = () => {
          this.imageLoader = false;
          this.image_url = URL.createObjectURL(this.file);
          this.imageErrors = "";
        };
        img.src = evt.target.result;
      };

      reader.onerror = (evt) => {
        console.error(evt);
      };
      // this.image_url = URL.createObjectURL(this.file);
    },
  },
};
</script>

<style scoped>
/* ── Card wrapper ── */
.settings-card {
  border-radius: 10px;
  overflow: hidden;
}

/* ── Header ── */
.settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #42a5f5 100%);
}

.header-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;
}

.header-subtitle {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.78);
  margin: 2px 0 0;
}

/* ── Section blocks ── */
.section-block {
  padding: 16px 20px 8px;
}


.section-label {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #546e7a;
  margin-bottom: 14px;
  padding-bottom: 6px;
  border-bottom: 1px dashed #e0e0e0;
}

/* ── Field cols spacing ── */
.field-col {
  padding: 0 8px 12px !important;
}

/* ── Logo upload ── */
.logo-upload-area {
  border: 1.5px dashed #bdbdbd;
  border-radius: 8px;
  padding: 12px 16px;
  background: #fafafa;
  transition: border-color 0.2s;
}

.logo-upload-area:not(.logo-disabled):hover {
  border-color: var(--v-primary-base);
  background: #f0f7ff;
}

.logo-disabled {
  opacity: 0.7;
}

.logo-preview-wrap {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.logo-preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo-placeholder {
  width: 100%;
  height: 100%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}


/* ── Content section extra bottom padding ── */
.section-block--content {
  padding-bottom: 16px;
}

/* ── Accordion ── */
.accordion-item {
  border: 1.5px solid #e8eaf0;
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.accordion-item--open {
  border-color: #7986cb;
  box-shadow: 0 2px 8px rgba(121, 134, 203, 0.15);
}

.accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  background: #f8f9fc;
  user-select: none;
  transition: background 0.15s;
}

.accordion-header:hover {
  background: #eef0fb;
}

.accordion-item--open .accordion-header {
  background: #eef0fb;
}

.accordion-title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #37474f;
}

.accordion-chevron {
  transition: transform 0.25s ease;
  flex-shrink: 0;
}

.accordion-chevron--open {
  transform: rotate(180deg);
}

.accordion-body {
  padding: 12px 14px 14px;
  background: #ffffff;
  border-top: 1px solid #e8eaf0;
}

/* ── Save footer ── */
.save-footer {
  position: sticky;
  bottom: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 10px 20px;
  background: linear-gradient(90deg, #f8f9fa 0%, #e8eaf6 100%);
  border-top: 1px solid #dee2e6;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

/* ── Global overrides ── */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
}
</style>
